<template>
  <div style="background-color: #eef3ea; min-height: 100vh;" class="pa-5">
    <PageLoader :storage="appLoading" />
    <ServerError v-if="ServerError" />
    
    <v-snackbar v-model="showSnackBar" color="#FF6907" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: #fff">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon style="color: #fff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <v-layout wrap justify-center class="mb-5">
      <v-flex xs12>
        <div class="d-flex align-center justify-space-between wrap">
          <div>
            <span class="heading" style="color: #173824; font-family: poppinsbold; font-size: 24px;">Analytics Dashboard</span>
            <div style="font-size: 13px; color: #718171;">Comprehensive statistics and analysis of human-wildlife conflicts</div>
          </div>
          <div style="width: 180px;">
            <v-select
              :items="dateitems"
              dense
              hide-details="true"
              outlined
              background-color="#ffffff"
              prepend-inner-icon="mdi-calendar-text"
              v-model="datefilter"
              class="chart-year-select"
            ></v-select>
          </div>
        </div>
      </v-flex>
    </v-layout>

    <!-- Stat Cards Section -->
    <div class="minister-dashboard__stats mb-5">
      <article
        v-for="(card, index) in cardData"
        :key="index"
        class="stat-card stat-card--clickable"
        @click="redirectToPage(card.route, card.title)"
      >
        <div class="stat-card__head">
          <span class="stat-card__label">{{ card.title }}</span>
          <v-icon color="#2e5a38" size="22">{{ card.icon }}</v-icon>
        </div>
        <div class="stat-card__value">{{ card.value || 0 }}</div>
        <div class="stat-card__action mt-3">
          <span>View Details</span>
          <v-icon small color="#2e5a38" class="ml-1">mdi-arrow-right</v-icon>
        </div>
      </article>
    </div>

    <!-- Cases Grid Section -->
    <div class="cases-grid mb-5">
      <article
        v-for="(card, index) in casecardData"
        :key="index"
        class="case-card"
        :class="'case-card--' + index"
      >
        <div class="case-card__title">{{ card.title }}</div>
        <div class="case-card__value">{{ card.value || 0 }}</div>
      </article>
    </div>

    <!-- Middle Section: District Reports and Pie Chart -->
    <div class="minister-dashboard__grid mb-5">
      <!-- Left Column: District List -->
      <section class="panel">
        <div class="panel__head">
          <div class="panel__title">
            <v-icon color="#2e5a38" size="24" class="mr-2">mdi-map-marker-radius</v-icon>
            Animal Conflict Reports by District
          </div>
          <div class="panel__subtitle">Detailed breakdown of incidents across Kerala districts</div>
        </div>
        <div>
          <div style="background: linear-gradient(135deg, #2e5a38 0%, #173824 100%); color: white; padding: 8px 16px; border-radius: 20px; font-size: 14px; font-weight: 600; display: inline-flex; align-items: center; box-shadow: 0 4px 12px rgba(46, 90, 56, 0.2); margin-bottom: 16px;">
            <v-icon color="white" small class="mr-2">mdi-earth</v-icon>
            Total Conflicts: {{ totalReports }}
          </div>
          <v-layout wrap class="district-list-container">
            <v-flex xs12 sm6 md6 lg6 v-for="district in activeDistricts" :key="district.name" pa-1>
              <div class="district-card" @click="redirectToDistrict(district.name)">
                <div class="d-flex align-center justify-space-between">
                  <div class="d-flex align-center">
                    <span class="district-color-dot mr-2" :style="{ backgroundColor: district.color }"></span>
                    <span class="district-name-text">{{ district.name }}</span>
                  </div>
                  <span class="district-count-val">{{ district.count }}</span>
                </div>
              </div>
            </v-flex>
          </v-layout>
        </div>
      </section>

      <!-- Right Column: Pie Chart -->
      <section class="panel">
        <div class="panel__head">
          <div class="panel__title">
            <v-icon color="#2e5a38" size="24" class="mr-2">mdi-chart-pie</v-icon>
            Conflicts by Animal Type
          </div>
          <div class="panel__subtitle">Distribution of conflict cases by animal species</div>
        </div>
        <div class="panel__chart d-flex align-center justify-center">
          <div v-if="flag" style="width: 100%;">
            <highcharts :options="highchartsOptions" :constructor-type="'chart'"></highcharts>
          </div>
          <div v-else style="width: 100%;">
            <v-skeleton-loader type="image" height="300" class="rounded-lg"></v-skeleton-loader>
          </div>
        </div>
      </section>
    </div>

    <!-- Bottom Section: Year-wise and Threat Level -->
    <div class="minister-dashboard__grid">
      <!-- Left Column: Year-wise chart -->
      <section class="panel">
        <div class="panel__head">
          <div class="panel__title">
            <v-icon color="#2e5a38" size="24" class="mr-2">mdi-history</v-icon>
            Year-wise Animal Conflict Reports
          </div>
          <div class="panel__subtitle">Annual breakdown of reported animal conflicts categorized by year</div>
        </div>
        <div class="panel__chart">
          <highcharts :options="yearlyHighchartsOptions"></highcharts>
        </div>
      </section>

      <!-- Right Column: Threat Level Line Chart -->
      <section class="panel">
        <div class="panel__head">
          <div class="d-flex align-center justify-space-between wrap">
            <div class="panel__title-group">
              <div class="panel__title">
                <v-icon color="#2e5a38" size="24" class="mr-2">mdi-chart-line-stacked</v-icon>
                Animal Conflicts by Threat Level
              </div>
              <div class="panel__subtitle">Visual breakdown of animal conflicts by threat level</div>
            </div>
            <div class="pt-2 pt-sm-0" style="width: 120px;">
              <v-select outlined dense :hide-details="true" :items="yearitems" v-model="lineyeardata"
                class="chart-year-select"></v-select>
            </div>
          </div>
        </div>
        <div class="panel__chart">
          <div v-if="flag1">
            <highcharts :options="linehighchartsOptions" ref="linehighchartsRef"></highcharts>
          </div>
          <div v-else>
            <v-skeleton-loader type="image" height="350" class="rounded-lg"></v-skeleton-loader>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showSnackBar: false,
      timeout: 5000,
      msg: null,
      flag: false,
      casecardData:[],
      flag1: false,
      dateitems: [
        "Today",
        "Yesterday",
        "Last 7 days",
        "Last 30 days",
        "Last 60 days",
        "Last 90 days",
      ],
      options: {
        chart: {
          type: "donut",
        },
        labels: [], // For the animal names
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200,
              },
              legend: {
                position: "bottom",
              },
            },
          },
        ],
      },
      series: [],
      datefilter: "Last 30 days",
      cardData: [
        {
          image: require("@/assets/images/humandeath.png"),
          title: "Human Deaths",
          value: "",
          vert: require("@/assets/images/vert1.png"),
          route: "/human-deaths",
          icon: "mdi-skull-crossbones",
        },
        {
          image: require("@/assets/images/animaldeath.png"),
          title: "Animal Deaths",
          value: "",
          vert: require("@/assets/images/vert2.png"),
          route: "/animal-deaths",
          icon: "mdi-paw-off",
        },
        {
          image: require("@/assets/images/humaninjury.png"),
          title: "Human Injury ",
          value: "",
          vert: require("@/assets/images/vert3.png"),
          route: "/human-injury",
          icon: "mdi-account-alert",
        },
        {
          image: require("@/assets/images/animalcapptured.png"),
          title: "Animals Captured",
          value: "",
          vert: require("@/assets/images/vert4.png"),
          route: "/animal-captures",
          icon: "mdi-lock",
        },
        {
          image: require("@/assets/images/animalreleased.png"),
          title: "Animals Released",
          value: "",
          vert: require("@/assets/images/vert5.png"),
          route: "/animal-released",
          icon: "mdi-rabbit",
        },
        {
          image: require("@/assets/images/animalinjured.png"),
          title: "Animals Injured",
          value: "",
          vert: require("@/assets/images/vert6.png"),
          route: "/animal-injuries",
          icon: "mdi-medical-bag",
        },
        {
          image: require("@/assets/images/elephantreported.png"),
          title: "Elephant Reported",
          value: "",
          vert: require("@/assets/images/vert7.png"),
          route: "/elephant-reports",
          icon: "mdi-elephant",
        },
        {
          image: require("@/assets/images/tigerreported.png"),
          title: "Tiger Reported",
          value: "",
          vert: require("@/assets/images/vert8.png"),
          route: "/tiger-reports",
          icon: "mdi-cat",
        },
      ],
      yearData: [],
      linechartseries: [
        {
          name: "Low threat",
          data: [50, 40, 60, 70, 80],
        },
        {
          name: "Moderate threat",
          data: [30.2, 30, 10, 50, 60],
        },
        {
          name: "High threat",
          data: [20, 25, 45, 67, 30],
        },
      ],
      linechartoptions: {
        chart: {
          height: 350,
          type: "line",
          zoom: {
            enabled: false,
          },
        },
        dataLabels: {
          enabled: false,
        },
        colors: ["#07E098", "#F6D635", "#F33232"],
      },
      yearitems: ["2024", "2025", "2026"],
      lineyeardata: new Date().getFullYear().toString(),
      totalAnimalDeaths: "",
      totalReleased: "",
      totalCaptured: "",
      totalHumanDeaths: "",
      totalHumanInjuries: "",
      totalconflictdata: 0,
      districtWiseCounts: [],
      totalReports: "",
      highchartsOptions: {
        chart: {
          type: "bar",
          height: 438,
          backgroundColor: "transparent",
          marginLeft: 168,
          marginRight: 72,
          spacingTop: 0,
          spacingRight: 12,
          spacingBottom: 0,
          spacingLeft: 0,
        },
        colors: ["#234c30", "#2e5a38", "#3d7c47", "#4f8c59", "#5f966d", "#70a57c", "#83b58f", "#97c5a3"],
        exporting: {
          enabled: false,
        },
        credits: {
          enabled: false,
        },
        title: {
          text: null,
        },
        legend: {
          enabled: false,
        },
        xAxis: {
          categories: [],
          title: {
            text: null,
          },
          lineColor: "#d8e4d2",
          tickLength: 0,
          labels: {
            style: {
              color: "#36533c",
              fontSize: "11px",
              fontWeight: "700",
            },
          },
        },
        yAxis: {
          min: 0,
          allowDecimals: false,
          title: {
            text: null,
            style: {
              color: "#6c7c6c",
              fontSize: "12px",
              fontWeight: "600",
            },
          },
          gridLineColor: "rgba(46, 90, 56, 0.1)",
          gridLineDashStyle: "ShortDot",
          labels: {
            style: {
              color: "#7c8a7b",
              fontSize: "11px",
            },
          },
        },
        tooltip: {
          useHTML: true,
          backgroundColor: "#ffffff",
          borderColor: "rgba(46, 90, 56, 0.18)",
          shadow: true,
          formatter: function () {
            return `
              <div style="font-size:13px;">
                <div style="font-weight:700;color:#173824;margin-bottom:4px;">${String(this.x || "")}</div>
                <div style="color:#36533c;">Reports: <b>${Number(this.y || 0).toLocaleString("en-IN")}</b></div>
              </div>
            `;
          },
        },
        plotOptions: {
          bar: {
            borderRadius: 8,
            pointWidth: 20,
            groupPadding: 0.14,
            pointPadding: 0.08,
            dataLabels: {
              enabled: true,
              inside: false,
              align: "left",
              x: 10,
              crop: false,
              overflow: "allow",
              formatter: function () {
                return Number(this.y || 0).toLocaleString("en-IN");
              },
              style: {
                color: "#173824",
                fontSize: "11px",
                fontWeight: "700",
                textOutline: "none",
              },
            },
          },
        },
        series: [
          {
            name: "Reports",
            colorByPoint: true,
            data: [],
          },
        ],
        responsive: [
          {
            condition: {
              maxWidth: 768,
            },
            chartOptions: {
              chart: {
                height: 460,
                marginLeft: 126,
                marginRight: 42,
              },
              plotOptions: {
                bar: {
                  pointWidth: 14,
                },
              },
            },
          },
        ],
      },
      seriesData: [],
      yearlyHighchartsOptions: {
        chart: {
          type: "column",
          height: 350,
          backgroundColor: "transparent",
        },
        credits: {
          enabled: false,
        },
        exporting: {
          enabled: false,
        },
        title: {
          text: null,
        },
        xAxis: {
          categories: [],
          lineColor: "#d5e1d1",
          labels: {
            style: {
              color: "#48604d",
              fontSize: "11px",
              fontWeight: "600",
            },
          },
        },
        yAxis: {
          min: 0,
          title: {
            text: "Reports",
          },
          gridLineColor: "rgba(46, 90, 56, 0.1)",
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          column: {
            borderRadius: 8,
            pointWidth: 28,
            colorByPoint: true,
            colors: ["#2e5a38", "#3f7d48", "#5b9965", "#89b58d", "#b8d1ba"],
            dataLabels: {
              enabled: true,
              style: {
                textOutline: "none",
                fontSize: "10px",
              },
            },
          },
        },
        series: [
          {
            name: "Reports",
            data: [],
          },
        ],
      },
      linehighchartsOptions: {
        chart: {
          type: "column",
          height: 350,
          backgroundColor: "transparent",
        },
        exporting: {
          enabled: false,
        },
        credits: {
          enabled: false,
        },
        title: {
          text: null,
        },
        xAxis: {
          categories: [],
          title: {
            text: "Months",
          },
          lineColor: "#d5e1d1",
          labels: {
            style: {
              color: "#48604d",
              fontSize: "11px",
              fontWeight: "600",
            },
          },
        },
        yAxis: {
          min: 0,
          title: {
            text: "Total Reports",
          },
          gridLineColor: "rgba(46, 90, 56, 0.1)",
          stackLabels: {
            enabled: true,
            style: {
              color: "#173824",
              fontSize: "10px",
              fontWeight: "700",
              textOutline: "none",
            },
          },
        },
        series: [
          {
            name: "High threat",
            data: [],
            color: "#F33232",
          },
          {
            name: "Medium threat",
            data: [],
            color: "#F6D635",
          },
          {
            name: "Low threat",
            data: [],
            color: "#07E098",
          },
        ],
        colors: ["#F33232", "#F6D635", "#07E098"],
        tooltip: {
          shared: true,
          borderColor: "rgba(46, 90, 56, 0.18)",
          backgroundColor: "#fff",
          borderRadius: 10,
          style: {
            fontSize: "12px",
          },
        },
        plotOptions: {
          column: {
            stacking: "normal",
            borderRadius: 6,
            pointWidth: 28,
            dataLabels: {
              enabled: true,
              style: {
                textOutline: "none",
                fontSize: "10px",
              },
            },
          },
        },
      },
    };
  },
  beforeMount() {
    this.getData();
    this.getCaseCountData();
    this.animalcountData();
    this.threatlevelData();
    this.districtCount();
    this.yearDatafull();
  },
  mounted() {
    const currentYear = new Date().getFullYear();
    if (!this.yearitems.includes(currentYear.toString())) {
      this.yearitems.push(currentYear.toString());
      this.yearitems.sort(); // Ensure the list is sorted
    }
  },
  computed: {
    appUser() {
      return this.$store.state.mainRole;
    },
    currentDate() {
      return new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    activeDistricts() {
      const allDistricts = [
        { name: 'Kollam', color: '#9B6AD0', index: 0 },
        { name: 'Trivandrum', color: '#7D0460', index: 1 },
        { name: 'Pathanamthitta', color: '#76CC1C', index: 2 },
        { name: 'Alappuzha', color: '#6BDA7C', index: 3 },
        { name: 'Kottayam', color: '#F05429', index: 4 },
        { name: 'Idukki', color: '#786D17', index: 5 },
        { name: 'Ernakulam', color: '#22D6C4', index: 6 },
        { name: 'Kannur', color: '#CD186B', index: 7 },
        { name: 'Kasaragod', color: '#EE7D47', index: 8 },
        { name: 'Kozhikode', color: '#FB38BB', index: 9 },
        { name: 'Malappuram', color: '#B83E89', index: 10 },
        { name: 'Palakkad', color: '#8ABC30', index: 11 },
        { name: 'Thrissur', color: '#EDED59', index: 12 },
        { name: 'Wayanad', color: '#563CF0', index: 13 }
      ];
      if (!this.districtWiseCounts) return [];
      
      return allDistricts.map(d => {
         const dCount = this.districtWiseCounts[d.index] ? this.districtWiseCounts[d.index].count : 0;
         return { ...d, count: dCount };
      }).filter(d => d.count > 0);
    },
  },
  watch: {
    lineyeardata() {
      this.threatlevelData();
    },
    datefilter() {
      this.getData();
      this.getCaseCountData();
      this.animalcountData();
      this.threatlevelData();
      this.districtCount();
      this.yearDatafull();
    },
  },
  methods: {
    redirectToDistrict(districtName) {
      if (this.appUser === "official") {
        this.$router.push({
          path: "/officers/district-details",
          query: { name: districtName, filter: this.datefilter },
        });
      } else {
        this.$router.push({
          path: "/admin/district-details",
          query: { name: districtName, filter: this.datefilter },
        });
      }
    },
    redirectToPage(route, title) {
      const basePath = this.appUser === "official" ? "/officers" : "/admin";
      this.$router.push({
        path: `${basePath}${route}`,
        query: { name: title, filter: this.datefilter },
      });
    },
    getData() {
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/hwc/dashboard/count",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          filter: this.datefilter,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.cardData[0].value = response.data.data.totalHumanDeaths;
            this.cardData[1].value = response.data.data.totalAnimalDeaths;
            this.cardData[2].value = response.data.data.totalHumanInjuries;
            this.cardData[3].value = response.data.data.totalCaptured;
            this.cardData[4].value = response.data.data.totalReleased;
            this.cardData[5].value = response.data.data.totalAnimalInjuries;
            this.cardData[6].value = response.data.data.totalElephantReports;
            this.cardData[7].value = response.data.data.totalTigerReports;
            this.appLoading = false;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    getCaseCountData() {
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/hwc/admin/dashboard/cases/count",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          filter: this.datefilter,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            const data = response.data.data;
            this.casecardData = [
              { title: "Total Cases", value: data.totalCases },
              { title: "Pending Cases", value: data.pendingCount },
              { title: "Resolved Cases", value: data.resolvedCount },
              { title: "Released Cases", value: data.releasedCount },
            ];
            this.appLoading = false;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    districtCount() {
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/hwc/reportcounts/districtwise",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          filter: this.datefilter,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.districtWiseCounts = response.data.data.districtWiseCounts;
            this.totalReports = response.data.data.totalReports;
            this.appLoading = false;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    yearDatafull() {
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/hwc/reportcounts/yearwise",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          filter: this.datefilter,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.yearData = response.data.data;
            this.yearlyHighchartsOptions.xAxis.categories = this.yearData.map((item) => item.year);
            this.yearlyHighchartsOptions.series[0].data = this.yearData.map((item) =>
              Number(item.totalReports || item.count || 0)
            );
            this.appLoading = false;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    animalcountData() {
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/hwc/animalsummary/reports",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          filter: this.datefilter,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            const animalReportSummary = response.data.data.animalReportSummary;
            const rankedAnimalReports = [...animalReportSummary]
              .map((item) => ({
                name: item.name,
                totalReports: Number(item.totalReports || 0),
              }))
              .sort((first, second) => second.totalReports - first.totalReports);
            this.series = rankedAnimalReports.map((item) => item.totalReports);
            this.options.labels = rankedAnimalReports.map((item) => item.name);
            this.totalconflictdata = response.data.data.totalReports;
            const seriesData = rankedAnimalReports.map((item) => ({
              name: item.name,
              y: item.totalReports,
            }));
            this.highchartsOptions.xAxis.categories = rankedAnimalReports.map((item) => item.name);
            this.highchartsOptions.series[0].data = seriesData;
            this.flag = true;
            this.appLoading = false;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    threatlevelData() {
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/hwc/threatlevel/reports",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          year: this.lineyeardata,
          filter: this.datefilter,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            const threatData = response.data.data;
            const months = threatData.map((item) => item.month);
            const highThreat = threatData.map((item) => {
              const level = item.threatLevels.find(
                (level) => level.threatlevel === "High"
              );
              return level ? level.totalReports : 0;
            });
            const mediumThreat = threatData.map((item) => {
              const level = item.threatLevels.find(
                (level) => level.threatlevel === "Medium"
              );
              return level ? level.totalReports : 0;
            });
            const lowThreat = threatData.map((item) => {
              const level = item.threatLevels.find(
                (level) => level.threatlevel === "Low"
              );
              return level ? level.totalReports : 0;
            });
            this.linechartseries = [
              { name: "High threat", data: highThreat },
              { name: "Moderate threat", data: mediumThreat },
              { name: "Low threat", data: lowThreat },
            ];
            this.linechartoptions = {
              ...this.linechartoptions,
              xaxis: {
                categories: months,
              },
            };
            this.linehighchartsOptions.xAxis.categories = months;
            this.linehighchartsOptions.series[0].data = highThreat;
            this.linehighchartsOptions.series[1].data = mediumThreat;
            this.linehighchartsOptions.series[2].data = lowThreat;
            this.flag1 = true;
            this.appLoading = false;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
/* Stat Cards */
.minister-dashboard__stats {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}

.stat-card {
  background: rgba(255, 255, 255, 0.96) !important;
  border: 1px solid rgba(34, 64, 35, 0.08) !important;
  border-radius: 20px !important;
  box-shadow: 0 16px 28px rgba(18, 42, 24, 0.08) !important;
  padding: 16px 18px !important;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.stat-card--clickable:hover {
  transform: translateY(-4px);
  background: #ffffff !important;
  border-color: rgba(46, 90, 56, 0.3) !important;
  box-shadow: 0 20px 35px rgba(18, 42, 24, 0.12) !important;
}

.stat-card__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stat-card__label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #6c7c6c;
}

.stat-card__value {
  margin-top: 12px;
  font-size: 30px;
  line-height: 1;
  font-weight: 700;
  color: #173824;
}

.stat-card__action {
  font-size: 11px;
  font-weight: 600;
  color: #2e5a38;
  display: inline-flex;
  align-items: center;
}

/* Grid Layout */
.minister-dashboard__grid {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 16px;
}

.panel {
  background: rgba(255, 255, 255, 0.96) !important;
  border: 1px solid rgba(34, 64, 35, 0.08) !important;
  border-radius: 20px !important;
  box-shadow: 0 16px 28px rgba(18, 42, 24, 0.08) !important;
  padding: 24px !important;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
}

.panel__head {
  margin-bottom: 8px;
}

.panel__title {
  font-size: 18px;
  font-weight: 700;
  color: #173824 !important;
  display: flex;
  align-items: center;
}

.panel__subtitle {
  font-size: 13px;
  color: #718171 !important;
  margin-top: 4px;
}

.panel__chart {
  flex: 1;
  min-height: 0;
}

/* Year Select - Modern Style */
.chart-year-select ::v-deep .v-input__slot {
  border-radius: 10px !important;
  background: #ffffff !important;
  border-color: rgba(46, 90, 56, 0.2) !important;
}

.chart-year-select.v-text-field--outlined.v-input--is-focused ::v-deep .v-input__slot {
  box-shadow: 0 0 0 3px rgba(46, 90, 56, 0.15) !important;
}

/* Cases Grid Section */
.cases-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}

.case-card {
  background: rgba(255, 255, 255, 0.96) !important;
  border: 1px solid rgba(34, 64, 35, 0.08) !important;
  border-radius: 16px !important;
  box-shadow: 0 10px 20px rgba(18, 42, 24, 0.05) !important;
  padding: 16px 20px !important;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-left: 5px solid #2e5a38 !important;
}

.case-card__title {
  font-size: 13px;
  font-weight: 600;
  color: #6c7c6c;
}

.case-card__value {
  font-size: 26px;
  font-weight: 700;
  color: #173824;
  margin-top: 6px;
}

.case-card--0 {
  border-left-color: #2e5a38 !important;
}
.case-card--1 {
  border-left-color: #f59e0b !important; /* Pending: Orange */
}
.case-card--2 {
  border-left-color: #10b981 !important; /* Resolved: Green */
}
.case-card--3 {
  border-left-color: #3b82f6 !important; /* Released: Blue */
}

/* District Card Styles */
.district-card {
  background: #f8fafc;
  border: 1px solid rgba(34, 64, 35, 0.05);
  border-radius: 12px;
  padding: 12px 14px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.district-card:hover {
  transform: translateY(-2px);
  background: #ffffff;
  border-color: rgba(46, 90, 56, 0.2);
  box-shadow: 0 4px 12px rgba(18, 42, 24, 0.06);
}

.district-color-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.district-name-text {
  font-family: poppinsmedium;
  font-size: 13px;
  color: #173824;
}

.district-count-val {
  font-family: poppinsbold;
  font-size: 13px;
  color: #173824;
  background: rgba(46, 90, 56, 0.08);
  padding: 2px 8px;
  border-radius: 10px;
}

.district-list-container {
  max-height: 420px;
  overflow-y: auto;
}

.heading {
  font-family: poppinsregular;
  font-weight: 600;
  font-size: 20px;
  line-height: 30px;
}

.yearhead {
  font-family: poppinsregular;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
}

.yearsub {
  font-family: poppinsregular;
  font-weight: 600;
  font-size: 20px;
  line-height: 32px;
}

@media (max-width: 1100px) {
  .minister-dashboard__grid {
    grid-template-columns: 1fr;
  }
}
</style>
